import { tracksApi } from 'services/tracks/tracks';
import store from 'redux-core/store';
import { getUnmatchedImportRowsCall, getDivisionAliasesCall } from './resolverServices';

export type UnmatchedApiRow = Record<string, unknown>;

export type StoredAliasDto = { id: number; vendorTitle: string; qwireTracksId: number };

/* Phase-1 progress, mirrored from the backend's MatchProgressDto. `total` is 0 while the
   spreadsheet is still being read (no row count exists yet), so a consumer must check it
   before computing a percentage. */
export type MatchProgress = { phase?: 'READING_FILE' | 'MATCHING'; processed: number; total: number };

type UnmatchedApiResponse = { status?: string; rows?: UnmatchedApiRow[]; progress?: MatchProgress };

type PrefetchResult = { rows: UnmatchedApiRow[]; divisionAliases: StoredAliasDto[]; timedOut: boolean };

type ProgressListener = (progress: MatchProgress | null) => void;

/* What both the prefetch and the resolver's own fallback load hand back: the eventual
   rows, a cancel, and a live progress feed. Progress is push-based rather than part of
   the resolved value because it has to reach the UI WHILE the promise is still pending —
   that is the entire point of it. */
export type UnmatchedSource = {
  promise: Promise<PrefetchResult>;
  cancel: () => void;
  getProgress: () => MatchProgress | null;
  subscribeProgress: (listener: ProgressListener) => () => void;
};

/* Match time scales with the catalog, not the file (a 95-row staging import took ~2 min
   pre-trgm-index against the 7.9M-row catalog, QW-2408), so poll with backoff under a
   generous budget instead of a fixed 60s burst — and report exhaustion distinctly:
   "still matching after 5 minutes" and "loaded, zero rows" are different states and the
   resolver UI must not conflate them.

   The first interval is short because the common case is now FAST: since the Pass-B
   query fix (2026-08-10) a typical import finishes matching in a couple of seconds, and a
   1500ms floor was adding more delay than the work it was waiting on. It still backs off
   to 10s, so a genuinely slow match costs the same handful of requests it always did. */
const POLL_BUDGET_MS = 5 * 60 * 1000;
const INITIAL_POLL_INTERVAL_MS = 400;
const MAX_POLL_INTERVAL_MS = 10000;
const POLL_BACKOFF_FACTOR = 1.5;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const loadUnmatchedAndAliases = async (
  jobId: string,
  isCancelled: () => boolean,
  onProgress?: (progress: MatchProgress | null) => void,
): Promise<PrefetchResult> => {
  const pollRows = async (): Promise<UnmatchedApiResponse | null> => {
    const startedAt = Date.now();
    let interval = INITIAL_POLL_INTERVAL_MS;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const response = (await getUnmatchedImportRowsCall({ jobId })) as unknown as UnmatchedApiResponse;
      if (isCancelled() || response?.status !== 'FILE_STRUCTURE_VALID') return response ?? {};
      onProgress?.(response?.progress ?? null);
      if (Date.now() - startedAt + interval > POLL_BUDGET_MS) return null;
      // eslint-disable-next-line no-await-in-loop
      await sleep(interval);
      interval = Math.min(interval * POLL_BACKOFF_FACTOR, MAX_POLL_INTERVAL_MS);
    }
  };

  const [response, divisionAliases] = await Promise.all([
    pollRows(),
    getDivisionAliasesCall({ jobId }) as Promise<StoredAliasDto[]>,
  ]);

  return { rows: response?.rows ?? [], divisionAliases, timedOut: response === null };
};

/* One source per load, shared by the prefetch and the resolver. The latest progress is
   retained (not just broadcast) because the prefetch usually starts BEFORE ResolverStep
   mounts — a subscriber arriving late must still see where the match already got to
   instead of waiting a whole poll interval for the next update. */
export const createUnmatchedSource = (jobId: string): UnmatchedSource => {
  let cancelled = false;
  let latest: MatchProgress | null = null;
  const listeners = new Set<ProgressListener>();

  const promise = loadUnmatchedAndAliases(
    jobId,
    () => cancelled,
    (progress) => {
      latest = progress;
      listeners.forEach((listener) => listener(progress));
    },
  );

  return {
    promise,
    cancel: () => {
      cancelled = true;
      listeners.clear();
    },
    getProgress: () => latest,
    subscribeProgress: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

type PrefetchEntry = { jobId: string; source: UnmatchedSource; unsubscribePartners: () => void };

let currentEntry: PrefetchEntry | null = null;

export const startUnmatchedPrefetch = (jobId: string): void => {
  if (currentEntry?.jobId === jobId) return;
  if (currentEntry) {
    currentEntry.source.cancel();
    currentEntry.unsubscribePartners();
  }

  const partnersSub = store.dispatch(tracksApi.endpoints.searchTracksPartners.initiate({ term: '', limit: 0 }));
  let partnersUnsubscribed = false;
  const unsubscribePartners = () => {
    if (partnersUnsubscribed) return;
    partnersUnsubscribed = true;
    partnersSub.unsubscribe();
  };

  const source = createUnmatchedSource(jobId);
  // Swallow here so a prefetch that's replaced or never consumed can't surface as an
  // unhandled rejection; the entry below keeps the original promise so a later consumer
  // still observes the rejection.
  source.promise.catch(() => {});

  currentEntry = { jobId, source, unsubscribePartners };
};

export const consumeUnmatchedPrefetch = (jobId: string): UnmatchedSource | null => {
  if (!currentEntry || currentEntry.jobId !== jobId) return null;
  const entry = currentEntry;
  currentEntry = null;
  entry.unsubscribePartners();
  return entry.source;
};

export const cancelUnmatchedPrefetch = (jobId?: string): void => {
  if (!currentEntry) return;
  if (jobId !== undefined && currentEntry.jobId !== jobId) return;
  currentEntry.source.cancel();
  currentEntry.unsubscribePartners();
  currentEntry = null;
};
